import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, Trash2, Plus, ChevronRight, Sparkles, Search, X, Calculator, CalendarRange, Flame, RotateCcw } from 'lucide-react';
import { BACKEND_URL } from '../../utils/api';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { pingAuraFuse } from '../../utils/auraPing';
import { FOODS, CATEGORIES } from '../../data/foods';
import { vibrate as nativeVibrate } from '../../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   THE ENERGY LEDGER — Imperium macro tracker
   Sophisticated "Old Money" nutrition console.
   Screens: setup → dashboard (quota vs consumed) → food picker.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#C5A059';
const GOLD_SOFT = '#8B6B2F';
const BG = '#050308';

/* Curated food library — per 100 g. Categorised by physiological benefit.
   Gender bias flag ('m'/'f'/'both') drives the recommended list. */

const scaleByGrams = (item, grams) => {
  const per = item.per || 100;
  const k = grams / per;
  const num = (v) => +(((Number(v) || 0)) * k).toFixed(1);
  return {
    name: `${item.name} · ${grams}g`,
    p:    num(item.p),
    ft:   num(item.ft),
    f:    num(item.f),
    c:    num(item.c),
    kcal: num(item.kcal),
  };
};

const Bar = ({ value, target, color }) => {
  const pct = target > 0 ? Math.min(100, (value / target) * 100) : 0;
  return (
    <div className="w-full h-1.5 rounded-full overflow-hidden"
      style={{ background: 'rgba(255,255,255,0.06)' }}>
      <div className="h-full rounded-full transition-all duration-500"
        style={{ width: `${pct}%`, background: color, boxShadow: `0 0 8px ${color}` }} />
    </div>
  );
};

const SetupRow = ({ label, options, value, onChange, testid }) => (
  <div className="mb-4">
    <p className="font-['Outfit'] uppercase tracking-[0.3em] mb-2"
      style={{ color: GOLD, fontSize: 10 }}>{label}</p>
    <div className="grid grid-cols-3 gap-1.5">
      {options.map(o => (
        <button key={o.v}
          onClick={() => onChange(o.v)}
          data-testid={`${testid}-${o.v}`}
          className="rounded-lg py-2 text-[11px] font-['Cinzel'] uppercase tracking-[0.2em] transition-all"
          style={{
            background: value === o.v
              ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`
              : 'rgba(255,255,255,0.03)',
            color: value === o.v ? '#0a0a0a' : 'rgba(255,255,255,0.7)',
            border: value === o.v ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)',
            boxShadow: value === o.v ? `0 0 16px rgba(197,160,89,0.35)` : 'none',
          }}>
          {o.label}
        </button>
      ))}
    </div>
  </div>
);

const MacroStat = ({ label, target, val, unit, color }) => (
  <div className="rounded-xl px-3 py-2.5"
    style={{
      background: 'linear-gradient(180deg, rgba(20,15,10,0.85), rgba(10,7,5,0.9))',
      border: `1px solid ${color}55`,
      boxShadow: `inset 0 0 12px ${color}18`,
    }}>
    <div className="flex items-baseline justify-between mb-1.5">
      <p className="font-['Outfit'] uppercase tracking-[0.28em]"
        style={{ color: color, fontSize: 9 }}>{label}</p>
      <p className="font-['Cinzel']" style={{ color: 'rgba(255,255,255,0.4)', fontSize: 9 }}>
        / {target}{unit}
      </p>
    </div>
    <p className="font-['Cinzel'] tracking-[0.06em] mb-1.5"
      style={{ color: '#FFE9A0', fontSize: 20 }}>
      {val.toFixed(1)}<span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', marginLeft: 3 }}>{unit}</span>
    </p>
    <Bar value={val} target={target} color={color} />
  </div>
);

/* ────────────── SETUP SCREEN ────────────── */
const Setup = ({ initial, onSaved }) => {
  const [gender, setGender] = useState(initial?.gender || 'male');
  const [weight, setWeight] = useState(initial?.weight_kg || '');
  const [mode, setMode] = useState(initial?.mode || 'normal');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const submit = async () => {
    const w = parseFloat(weight);
    if (!w || w < 20 || w > 300) { setError('Weight must be between 20 and 300 kg.'); return; }
    setSaving(true); setError('');
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/energy/profile`, {
        gender, weight_kg: w, mode,
      });
      onSaved(data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-5 py-6" data-testid="energy-setup">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <p className="font-['Outfit'] uppercase tracking-[0.35em] mb-1" style={{ color: GOLD, fontSize: 10 }}>
            Initialization
          </p>
          <h2 className="font-['Cinzel'] tracking-[0.14em]"
            style={{ color: '#FFE9A0', fontSize: 22, textShadow: '0 0 14px rgba(197,160,89,0.35)' }}>
            The Imperium Ledger
          </h2>
          <p className="mt-3 font-['Outfit'] text-[11px] leading-relaxed"
            style={{ color: 'rgba(255,255,255,0.55)' }}>
            Declare your vessel. The Ledger shall compute a daily quota of protein, fats,
            fiber and carbohydrates — calibrated to your physiology and dominion.
          </p>
        </div>

        <div className="mb-4">
          <p className="font-['Outfit'] uppercase tracking-[0.3em] mb-2"
            style={{ color: GOLD, fontSize: 10 }}>Gender</p>
          <div className="grid grid-cols-2 gap-1.5">
            {[{ v: 'male', label: 'Male' }, { v: 'female', label: 'Female' }].map(o => (
              <button key={o.v}
                onClick={() => setGender(o.v)}
                data-testid={`energy-gender-${o.v}`}
                className="rounded-lg py-2 text-[11px] font-['Cinzel'] uppercase tracking-[0.2em] transition-all"
                style={{
                  background: gender === o.v
                    ? `linear-gradient(180deg, ${GOLD}, ${GOLD_SOFT})`
                    : 'rgba(255,255,255,0.03)',
                  color: gender === o.v ? '#0a0a0a' : 'rgba(255,255,255,0.7)',
                  border: gender === o.v ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)',
                  boxShadow: gender === o.v ? `0 0 16px rgba(197,160,89,0.35)` : 'none',
                }}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <p className="font-['Outfit'] uppercase tracking-[0.3em] mb-2"
            style={{ color: GOLD, fontSize: 10 }}>Weight · Kilograms</p>
          <input
            type="number"
            inputMode="decimal"
            value={weight}
            onChange={(e) => setWeight(e.target.value)}
            placeholder="e.g. 72"
            data-testid="energy-weight-input"
            className="w-full rounded-lg px-4 py-3 font-['Cinzel'] tracking-[0.14em] text-center outline-none"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: `1px solid ${GOLD}`,
              color: '#FFE9A0',
              fontSize: 20,
              boxShadow: `inset 0 0 12px rgba(197,160,89,0.08), 0 0 12px rgba(197,160,89,0.12)`,
            }}
          />
        </div>

        <SetupRow label="Mode" testid="energy-mode"
          value={mode} onChange={setMode}
          options={[{ v: 'normal', label: 'Normal' }, { v: 'active', label: 'Active' }, { v: 'athlete', label: 'Athlete' }]} />

        {error && (
          <p className="mb-3 text-[11px] font-['Outfit'] text-center"
            style={{ color: '#e88a7a' }}>{error}</p>
        )}

        <button
          onClick={submit}
          disabled={saving}
          data-testid="energy-calculate-btn"
          className="w-full rounded-xl py-3 font-['Cinzel'] uppercase tracking-[0.3em] transition-all"
          style={{
            background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
            color: '#0a0a0a',
            fontSize: 12,
            boxShadow: '0 0 20px rgba(197,160,89,0.4), inset 0 1px 0 rgba(255,255,255,0.3)',
            opacity: saving ? 0.6 : 1,
          }}>
          {saving ? 'Computing…' : 'Compute Quota →'}
        </button>

        <p className="mt-6 text-[9px] leading-relaxed font-['Outfit'] text-center"
          style={{ color: 'rgba(255,255,255,0.35)' }}>
          Disclaimer: This data is for tracking purposes only and does not replace
          professional medical advice. Always consult a healthcare professional.
        </p>
      </div>
    </div>
  );
};

/* ────────────── DASHBOARD ────────────── */
const Dashboard = ({ today, onReset, onOpenPicker, onDelete }) => {
  const { quota, consumed, items } = today;

  const remaining = {
    p:  Math.max(0, quota.protein_g - consumed.protein_g),
    ft: Math.max(0, quota.fat_g     - consumed.fat_g),
    f:  Math.max(0, quota.fiber_g   - consumed.fiber_g),
    c:  Math.max(0, quota.carbs_g   - consumed.carbs_g),
    kcal: Math.max(0, quota.tdee_kcal - consumed.kcal),
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 pb-8" data-testid="energy-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="font-['Outfit'] uppercase tracking-[0.28em]" style={{ color: GOLD, fontSize: 9 }}>
            Daily Imperium Quota
          </p>
          <p className="font-['Cinzel'] tracking-[0.12em]"
            style={{ color: '#FFE9A0', fontSize: 16 }}>
            {quota.gender[0].toUpperCase() + quota.gender.slice(1)} · {quota.weight_kg} kg · {quota.mode[0].toUpperCase() + quota.mode.slice(1)}
          </p>
        </div>
        <button onClick={onReset}
          data-testid="energy-reset-btn"
          className="text-[9px] font-['Outfit'] uppercase tracking-[0.25em] px-2.5 py-1 rounded-full"
          style={{ color: GOLD, border: `1px solid ${GOLD}40` }}>
          Edit
        </button>
      </div>

      {/* Calories headline */}
      <div className="rounded-2xl px-4 py-4 mb-3 relative overflow-hidden"
        style={{
          background: 'radial-gradient(ellipse at 20% 20%, rgba(197,160,89,0.16) 0%, rgba(0,0,0,0) 60%), linear-gradient(180deg, rgba(20,15,10,0.9), rgba(5,3,8,0.95))',
          border: `1px solid ${GOLD}55`,
          boxShadow: '0 0 20px rgba(197,160,89,0.15)',
        }}>
        <div className="flex items-center justify-between mb-2">
          <p className="font-['Outfit'] uppercase tracking-[0.3em]" style={{ color: GOLD, fontSize: 10 }}>
            Caloric Balance
          </p>
          <Sparkles size={14} color={GOLD}/>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="font-['Cinzel']" style={{ color: '#FFE9A0', fontSize: 32, textShadow: '0 0 14px rgba(197,160,89,0.4)' }}>
            {consumed.kcal.toFixed(0)}
          </span>
          <span className="font-['Cinzel']" style={{ color: 'rgba(255,255,255,0.5)', fontSize: 14 }}>
            / {quota.tdee_kcal} kcal
          </span>
          <span className="ml-auto font-['Outfit'] uppercase tracking-[0.2em]"
            style={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
            {remaining.kcal.toFixed(0)} left
          </span>
        </div>
        <div className="mt-2"><Bar value={consumed.kcal} target={quota.tdee_kcal} color={GOLD}/></div>
      </div>

      {/* Macro grid */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        <MacroStat label="Protein"  target={quota.protein_g} val={consumed.protein_g} unit="g" color="#E85A5A" />
        <MacroStat label="Fat"      target={quota.fat_g}     val={consumed.fat_g}     unit="g" color="#F1B850" />
        <MacroStat label="Carbs"    target={quota.carbs_g}   val={consumed.carbs_g}   unit="g" color="#5DD4FF" />
        <MacroStat label="Fiber"    target={quota.fiber_g}   val={consumed.fiber_g}   unit="g" color="#7AC96A" />
      </div>

      {/* Add food */}
      <button
        onClick={onOpenPicker}
        data-testid="energy-add-food-btn"
        className="w-full rounded-xl py-2.5 mb-3 font-['Cinzel'] uppercase tracking-[0.28em] flex items-center justify-center gap-2"
        style={{
          background: 'linear-gradient(180deg, rgba(197,160,89,0.15) 0%, rgba(197,160,89,0.05) 100%)',
          border: `1px solid ${GOLD}`,
          color: GOLD,
          fontSize: 11,
          boxShadow: '0 0 12px rgba(197,160,89,0.2)',
        }}>
        <Plus size={14}/> Inscribe Sustenance
      </button>

      {/* Consumed list */}
      {items.length > 0 && (
        <div className="mb-3">
          <p className="font-['Outfit'] uppercase tracking-[0.28em] mb-2"
            style={{ color: GOLD, fontSize: 9 }}>Today&apos;s Ledger</p>
          <div className="space-y-1.5" data-testid="energy-items-list">
            {items.slice().reverse().map((it) => (
              <div key={it.id}
                className="rounded-lg px-3 py-2 flex items-center justify-between"
                style={{
                  background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)',
                }}>
                <div className="min-w-0 flex-1">
                  <p className="font-['Cinzel'] truncate" style={{ color: '#FFE9A0', fontSize: 12 }}>
                    {it.name}
                  </p>
                  <p className="font-['Outfit'] mt-0.5"
                    style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9 }}>
                    P {it.p}g · Ft {it.ft}g · C {it.c}g · F {it.f}g · {it.kcal} kcal
                  </p>
                </div>
                <button onClick={() => onDelete(it.id)}
                  data-testid={`energy-item-delete-${it.id}`}
                  className="ml-2 p-1.5 rounded-full flex-shrink-0"
                  style={{ background: 'rgba(232,90,90,0.1)', border: '1px solid rgba(232,90,90,0.25)' }}>
                  <Trash2 size={12} color="#E85A5A"/>
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="mt-4 text-[9px] leading-relaxed font-['Outfit'] text-center"
        style={{ color: 'rgba(255,255,255,0.35)' }}>
        Disclaimer: For tracking purposes only. Consult a healthcare professional.
      </p>
    </div>
  );
};

/* ────────────── FOOD PICKER ────────────── */

const FoodPicker = ({ gender, onBack, onLog }) => {
  const [selected, setSelected] = useState(null);
  const [grams, setGrams] = useState('');
  const [logging, setLogging] = useState(false);
  const [tab, setTab] = useState('protein');
  const [query, setQuery] = useState('');
  const [globalMode, setGlobalMode] = useState(false); // toggle for USDA search
  const [globalResults, setGlobalResults] = useState([]);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalError, setGlobalError] = useState('');

  // Debounced USDA search — only fires when globalMode is on and query >= 2 chars
  useEffect(() => {
    if (!globalMode) { setGlobalResults([]); setGlobalError(''); return; }
    const q = query.trim();
    if (q.length < 2) { setGlobalResults([]); setGlobalError(''); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setGlobalLoading(true); setGlobalError('');
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/api/usda/search`, { params: { q } }
        );
        if (!cancelled) setGlobalResults(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          const detail = e?.response?.data?.detail;
          setGlobalError(typeof detail === 'string' ? detail : 'Search unavailable. Try again.');
          setGlobalResults([]);
        }
      } finally {
        if (!cancelled) setGlobalLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [query, globalMode]);

  const list = useMemo(() => {
    if (globalMode) {
      // USDA results are already global; still allow tab filter when no query
      return globalResults;
    }
    const q = query.trim().toLowerCase();
    const filtered = FOODS.filter((it) => {
      // If searching, ignore tab and search across all foods
      if (q) return it.name.toLowerCase().includes(q) || (it.cat || '').toLowerCase().includes(q);
      return it.g === tab;
    });
    const priority = (item) => {
      if (gender === 'female') return item.bias === 'f' ? 0 : (item.bias === 'both' ? 1 : 2);
      if (gender === 'male')   return item.bias === 'm' ? 0 : (item.bias === 'both' ? 1 : 2);
      return item.bias === 'both' ? 0 : 1;
    };
    return filtered.sort((a, b) => priority(a) - priority(b));
  }, [gender, tab, query, globalMode, globalResults]);

  const defaultPortion = (item) => item.per || 100;
  const currentGrams = parseFloat(grams) || defaultPortion(selected || {});
  const scaled = selected ? scaleByGrams(selected, currentGrams) : null;

  const submit = async () => {
    if (!scaled) return;
    setLogging(true);
    await onLog({
      name: scaled.name,
      protein_g: scaled.p,
      fat_g:     scaled.ft,
      fiber_g:   scaled.f,
      carbs_g:   scaled.c,
      kcal:      scaled.kcal,
    });
    setLogging(false);
  };

  const activeCat = CATEGORIES.find((c) => c.g === tab) || CATEGORIES[0];

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="energy-picker">
      {/* Header */}
      <div className="px-4 pt-3 pb-2 flex items-center gap-3 flex-shrink-0">
        <button onClick={onBack}
          data-testid="energy-picker-back"
          className="p-1.5 rounded-full"
          style={{ background: 'rgba(197,160,89,0.08)', border: `1px solid ${GOLD}40` }}>
          <ChevronLeft size={14} color={GOLD}/>
        </button>
        <p className="font-['Cinzel'] tracking-[0.14em]"
          style={{ color: '#FFE9A0', fontSize: 15 }}>
          Choose Sustenance
        </p>
      </div>

      {/* Search */}
      <div className="px-4 pb-2 flex-shrink-0">
        <div className="relative">
          <Search size={13}
            style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.4)' }}/>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={globalMode ? 'Search 300k+ global foods…' : 'Search local library…'}
            data-testid="energy-food-search"
            className="w-full rounded-lg pl-8 pr-8 py-2 font-['Outfit'] outline-none"
            style={{
              background: 'rgba(255,255,255,0.04)',
              border: `1px solid ${GOLD}40`,
              color: '#FFE9A0',
              fontSize: 12,
              letterSpacing: '0.02em',
            }}
          />
          {query && (
            <button
              onClick={() => setQuery('')}
              data-testid="energy-food-search-clear"
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
              className="p-1 rounded-full"
              aria-label="Clear search">
              <X size={12} color="rgba(255,255,255,0.55)"/>
            </button>
          )}
        </div>
      </div>

      {/* Global-database toggle */}
      <div className="px-4 pb-1.5 flex-shrink-0 flex items-center gap-2">
        <button
          onClick={() => { setGlobalMode(!globalMode); setSelected(null); }}
          data-testid="energy-food-global-toggle"
          className="flex-1 rounded-lg py-1.5 font-['Cinzel'] uppercase tracking-[0.18em] transition-all"
          style={{
            background: globalMode
              ? `linear-gradient(180deg, ${GOLD}22, ${GOLD}08)`
              : 'rgba(255,255,255,0.03)',
            border: globalMode ? `1px solid ${GOLD}` : '1px solid rgba(255,255,255,0.08)',
            color: globalMode ? GOLD : 'rgba(255,255,255,0.55)',
            fontSize: 9,
            boxShadow: globalMode ? `0 0 10px ${GOLD}33` : 'none',
          }}>
          {globalMode ? '★ Global Database · ON' : 'Search Global Database (USDA)'}
        </button>
      </div>

      {/* Category tabs — hidden while searching or in global mode */}
      {!query && !globalMode && (
        <div className="px-3 pb-1.5 flex-shrink-0 flex gap-1.5" data-testid="energy-food-tabs">
          {CATEGORIES.map((c) => (
            <button key={c.g}
              onClick={() => setTab(c.g)}
              data-testid={`energy-food-tab-${c.g}`}
              className="flex-1 rounded-lg py-1.5 font-['Cinzel'] uppercase tracking-[0.16em] transition-all"
              style={{
                background: tab === c.g
                  ? `linear-gradient(180deg, ${c.color}22, ${c.color}08)`
                  : 'rgba(255,255,255,0.03)',
                border: tab === c.g ? `1px solid ${c.color}` : '1px solid rgba(255,255,255,0.06)',
                color: tab === c.g ? c.color : 'rgba(255,255,255,0.55)',
                fontSize: 10,
                boxShadow: tab === c.g ? `0 0 10px ${c.color}33` : 'none',
              }}>
              {c.label}
            </button>
          ))}
        </div>
      )}

      {/* Helper caption */}
      <p className="px-4 mb-2 mt-1 font-['Outfit'] text-[9px]"
        style={{ color: 'rgba(255,255,255,0.5)' }}>
        {globalMode
          ? (globalLoading
              ? 'Consulting USDA archives…'
              : globalError
                ? globalError
                : query.trim().length < 2
                  ? 'Type 2+ characters to search 300k+ global foods (branded + generic).'
                  : `${list.length} result${list.length === 1 ? '' : 's'} · USDA FoodData Central`)
          : query
            ? `${list.length} result${list.length === 1 ? '' : 's'} across all groups`
            : gender === 'female'
              ? `Iron & calcium sources first — ${activeCat.label} category`
              : gender === 'male'
                ? `Leucine & zinc sources first — ${activeCat.label} category`
                : `${activeCat.label} category — balanced sources first`}
      </p>

      {/* Scrollable list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {list.length === 0 ? (
          <p className="text-center font-['Outfit'] mt-8"
            style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>
            {globalMode && query.trim().length >= 2 && !globalLoading && !globalError
              ? `No global results for "${query}".`
              : globalMode && query.trim().length < 2
                ? 'Awaiting your query…'
                : query
                  ? `No sustenance matches "${query}".`
                  : 'No items in this category.'}
          </p>
        ) : list.map((f) => (
          <button key={(f.fdc_id || '') + f.name}
            onClick={() => { setSelected(f); setGrams(String(defaultPortion(f))); }}
            data-testid={f.fdc_id
              ? `energy-food-usda-${f.fdc_id}`
              : `energy-food-${f.name.replace(/[^a-zA-Z0-9]/g, '-')}`}
            className="w-full text-left rounded-lg px-3 py-2 mb-1.5 flex items-center justify-between transition-all"
            style={{
              background: selected?.name === f.name
                ? 'linear-gradient(180deg, rgba(197,160,89,0.18), rgba(197,160,89,0.04))'
                : 'rgba(255,255,255,0.02)',
              border: selected?.name === f.name
                ? `1px solid ${GOLD}`
                : '1px solid rgba(255,255,255,0.06)',
            }}>
            <div className="min-w-0 flex-1">
              <p className="font-['Cinzel'] truncate"
                style={{ color: '#FFE9A0', fontSize: 12 }}>
                {f.name}
                {f.brand ? <span style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10, marginLeft: 4 }}> · {f.brand}</span> : null}
              </p>
              <p className="font-['Outfit'] mt-0.5 truncate"
                style={{ color: 'rgba(255,255,255,0.45)', fontSize: 9 }}>
                {f.cat || '—'} · P {(f.p ?? 0)}g · {(f.kcal ?? 0)} kcal <span style={{ color: 'rgba(255,255,255,0.3)' }}>/ {f.per || 100}g</span>
              </p>
            </div>
            <ChevronRight size={14} color="rgba(255,255,255,0.35)"/>
          </button>
        ))}
      </div>

      {/* Portion input drawer — with safe-area padding so Inscribe button
          is fully visible above the mobile bottom nav / gesture bar. */}
      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ y: 200, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 200, opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 220 }}
            className="flex-shrink-0 px-4 pt-3 border-t"
            style={{
              background: 'linear-gradient(180deg, rgba(15,10,7,0.98), rgba(5,3,8,1))',
              borderColor: `${GOLD}30`,
              paddingBottom: 'calc(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)) + 16px)',
            }}>
            <p className="font-['Cinzel'] mb-2 truncate"
              style={{ color: '#FFE9A0', fontSize: 13 }}>{selected.name}</p>
            <div className="flex items-center gap-2 mb-2">
              <input
                type="number"
                inputMode="decimal"
                value={grams}
                onChange={(e) => setGrams(e.target.value)}
                data-testid="energy-portion-input"
                placeholder={`${defaultPortion(selected)}`}
                className="flex-1 rounded-lg px-3 py-2 font-['Cinzel'] tracking-[0.1em] text-center outline-none"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: `1px solid ${GOLD}80`,
                  color: '#FFE9A0',
                  fontSize: 16,
                }}
              />
              <span className="font-['Outfit'] uppercase tracking-[0.25em]"
                style={{ color: GOLD, fontSize: 10 }}>grams</span>
            </div>
            {scaled && (
              <p className="font-['Outfit'] mb-2"
                style={{ color: 'rgba(255,255,255,0.55)', fontSize: 10 }}>
                Will inscribe: <span style={{ color: '#FFE9A0' }}>
                  {scaled.kcal} kcal</span> · P {scaled.p}g · Ft {scaled.ft}g · C {scaled.c}g · F {scaled.f}g
              </p>
            )}
            <button
              onClick={submit}
              disabled={logging}
              data-testid="energy-log-confirm-btn"
              className="w-full rounded-xl py-2.5 font-['Cinzel'] uppercase tracking-[0.28em]"
              style={{
                background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                color: '#0a0a0a',
                fontSize: 11,
                boxShadow: '0 0 16px rgba(197,160,89,0.35)',
                opacity: logging ? 0.6 : 1,
              }}>
              {logging ? 'Inscribing…' : 'Inscribe →'}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/* ────────────── ROOT MODAL ────────────── */
/* ═══════════════════════════════════════════════════════════════════
   MASTER MENU — 2 cards: Calculator | Daily Fuel Fuse
   ═══════════════════════════════════════════════════════════════════ */
const MenuCard = ({ Icon, label, title, sub, onOpen, testid, accent = GOLD }) => (
  <button type="button" onClick={onOpen}
    className="w-full text-left p-5 rounded-2xl transition-transform active:scale-[0.98]"
    style={{
      background: `linear-gradient(160deg, rgba(255,255,255,0.03), ${BG})`,
      border: `1.5px solid ${accent}55`,
      boxShadow: `0 0 20px ${accent}22, inset 0 0 12px rgba(255,255,255,0.02)`,
      minHeight: 128,
    }}
    data-testid={testid}>
    <div className="flex items-center gap-4">
      <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
        style={{
          background: `radial-gradient(circle, ${accent}44, transparent 70%)`,
          border: `1px solid ${accent}`,
          boxShadow: `inset 0 0 10px ${accent}44`,
        }}>
        <Icon size={22} color="#FFE9A0"/>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.28em]" style={{ color: accent }}>
          {label}
        </p>
        <h3 className="font-['Cormorant_Garamond'] text-2xl leading-tight" style={{ color: '#FFE9A0' }}>
          {title}
        </h3>
        <p className="font-['Cormorant_Garamond'] italic text-xs mt-0.5" style={{ color: `${GOLD}AA` }}>
          {sub}
        </p>
      </div>
    </div>
  </button>
);

const MasterMenu = ({ today, tab, setTab, dashboardNode, fuseNode }) => {
  const target = today?.quota?.tdee_kcal || 0;
  const consumed = today?.consumed?.kcal || 0;
  const pct = target > 0 ? Math.round((consumed / target) * 100) : 0;
  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid="fuel-master-menu">
      {/* Compact quota strip */}
      <div className="px-4 pt-2 pb-1.5 flex-shrink-0">
        <p className="text-center font-['Cormorant_Garamond'] italic text-[11px]"
          style={{ color: `${GOLD}AA` }}>
          Today · {consumed.toFixed(0)} / {target.toFixed(0)} kcal · {pct}%
        </p>
      </div>

      {/* Sub-tab bar */}
      <div className="px-3 pb-2 flex-shrink-0" data-testid="fuel-subtabs">
        <div className="grid grid-cols-2 gap-1.5 rounded-full p-1"
          style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}33` }}>
          {[
            { id: 'quota', label: 'Daily Imperium Quota', Icon: Calculator },
            { id: 'fuse',  label: 'Daily Fuel Fuse',      Icon: CalendarRange },
          ].map((t) => {
            const active = tab === t.id;
            return (
              <button key={t.id} type="button" onClick={() => setTab(t.id)}
                className="flex items-center justify-center gap-1.5 py-1.5 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.2em] transition-all"
                style={{
                  background: active ? `linear-gradient(135deg, #FFE9A0, ${GOLD_SOFT})` : 'transparent',
                  color: active ? '#050308' : '#FFE9A0',
                  boxShadow: active ? `0 0 10px ${GOLD}77` : 'none',
                }}
                data-testid={`fuel-subtab-${t.id}`}>
                <t.Icon size={11}/> {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Active tab content */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {tab === 'quota' ? dashboardNode : fuseNode}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════
   DAILY FUEL FUSE — schedule + calendar (localStorage)
   ═══════════════════════════════════════════════════════════════════ */
const FUEL_FUSE_KEY = 'fuel_fuse_v1';
const FUSE_PERIODS  = [
  { days: 21,  label: '21 Days' },
  { days: 30,  label: '30 Days' },
  { days: 66,  label: '66 Days' },
  { days: 100, label: '100 Days' },
];

const loadFuse = () => {
  try {
    const raw = localStorage.getItem(FUEL_FUSE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
};
const saveFuse = (s) => {
  try { localStorage.setItem(FUEL_FUSE_KEY, JSON.stringify(s)); } catch (_) { /* quota */ }
};
const todayKey = () => new Date().toISOString().slice(0, 10);
const dateKeyOf = (start, dayIdx) => {
  const d = new Date(start);
  d.setDate(d.getDate() + dayIdx);
  return d.toISOString().slice(0, 10);
};
const haptic = (ms = 15) => {
  try { nativeVibrate(ms); } catch (_) { /* noop */ }
};

const FuelFuse = ({ today, onBack }) => {
  const target = today?.quota?.tdee_kcal || 0;
  const consumed = today?.consumed?.kcal || 0;
  const pct = target > 0 ? Math.min(999, Math.round((consumed / target) * 100)) : 0;
  const canFuse = pct >= 80;

  const [state, setState] = useState(() => loadFuse());

  useEffect(() => { if (state) saveFuse(state); }, [state]);

  const forge = (days) => {
    setState({
      cycleDays: days,
      startDate: new Date().toISOString(),
      fusedDays: {},
    });
    haptic(25);
  };

  const fuseToday = () => {
    if (!state || !canFuse) return;
    const key = todayKey();
    if (state.fusedDays?.[key]) return;
    setState((prev) => ({
      ...prev,
      fusedDays: {
        ...(prev.fusedDays || {}),
        [key]: {
          kcal: Math.round(consumed),
          target: Math.round(target),
          percent: pct,
          fused_at: new Date().toISOString(),
        },
      },
    }));
    pingAuraFuse('fuel');
    haptic(30);
  };

  const resetCycle = () => {
    if (!window.confirm('Reset this cycle? All fused days will be cleared.')) return;
    setState(null);
    try { localStorage.removeItem(FUEL_FUSE_KEY); } catch (_) { /* noop */ }
  };

  const barColor = pct >= 80 ? '#7AC96A' : pct >= 60 ? '#F1B850' : '#E85A5A';

  // Cycle state
  const fusedCount = state ? Object.keys(state.fusedDays || {}).length : 0;
  const total      = state?.cycleDays || 0;
  const complete   = state && fusedCount >= total;
  const tKey       = todayKey();
  const fusedToday = state && !!state.fusedDays?.[tKey];

  // Show late-day nudge after 8 PM local
  const nowHour = new Date().getHours();
  const isLate = nowHour >= 20 || nowHour < 4;

  return (
    <div className="flex-1 overflow-y-auto px-5 pt-3 pb-8" data-testid="fuel-fuse-view">
      {/* Today's summary */}
      <div className="mt-4 rounded-2xl p-4"
        style={{
          background: 'linear-gradient(160deg, rgba(255,255,255,0.03), rgba(0,0,0,0.4))',
          border: `1px solid ${GOLD}44`,
        }}
        data-testid="fuel-fuse-today">
        <div className="flex items-baseline justify-between">
          <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.24em]" style={{ color: `${GOLD}CC` }}>
            Today · {tKey}
          </p>
          <p className="font-['Cormorant_Garamond'] text-2xl leading-none"
            style={{ color: barColor }} data-testid="fuel-fuse-percent">
            {pct}%
          </p>
        </div>
        <div className="mt-2 h-2 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.05)' }}>
          <div style={{
            width: `${Math.min(100, pct)}%`, height: '100%',
            background: `linear-gradient(90deg, ${barColor}, ${barColor}CC)`,
            boxShadow: `0 0 8px ${barColor}AA`,
            transition: 'width 0.4s ease',
          }}/>
        </div>
        <div className="mt-2 flex items-baseline justify-between">
          <p className="font-['Cormorant_Garamond'] text-[13px]" style={{ color: '#FFE9A0' }}>
            <span style={{ color: barColor }}>{Math.round(consumed)}</span> / {Math.round(target)} kcal
          </p>
          <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.2em]"
            style={{ color: pct >= 80 ? '#7AC96A' : `${GOLD}88` }}>
            {pct >= 80 ? '● Fusable' : `${80 - pct}% to seal`}
          </p>
        </div>
      </div>

      {/* Cycle picker OR active */}
      {!state ? (
        <div className="mt-4 rounded-2xl p-4"
          style={{ border: `1px dashed ${GOLD}66`, background: `${GOLD}0A` }}
          data-testid="fuel-fuse-forge-picker">
          <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.24em]" style={{ color: GOLD }}>
            Forge a Cycle
          </p>
          <p className="mt-1 font-['Cormorant_Garamond'] italic text-[12px]" style={{ color: `${GOLD}AA` }}>
            Commit to a stretch — seal each 80%+ day. Only today can be fused each night.
          </p>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {FUSE_PERIODS.map((p) => (
              <button key={p.days} type="button" onClick={() => forge(p.days)}
                className="py-2 rounded-lg font-['Outfit'] uppercase text-[10px] tracking-[0.2em]"
                style={{
                  background: `linear-gradient(135deg, #FFE9A0, ${GOLD_SOFT})`,
                  color: '#050308', boxShadow: `0 0 8px ${GOLD}66`,
                }}
                data-testid={`fuel-fuse-forge-${p.days}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-2xl p-4"
          style={{ border: `1px solid ${GOLD}55`, background: 'rgba(0,0,0,0.35)' }}
          data-testid="fuel-fuse-active">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Flame size={13} color={GOLD}/>
              <p className="font-['Outfit'] text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
                Cycle · {total} days · {fusedCount}/{total} fused
              </p>
            </div>
            <button type="button" onClick={resetCycle}
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ border: `1px solid ${GOLD}55`, color: `${GOLD}CC` }}
              aria-label="Reset cycle"
              data-testid="fuel-fuse-reset">
              <RotateCcw size={11}/>
            </button>
          </div>

          {/* Calendar grid */}
          <div className="mt-3 grid grid-cols-7 gap-1" data-testid="fuel-fuse-grid">
            {Array.from({ length: total }).map((_, i) => {
              const dk = dateKeyOf(state.startDate, i);
              const isToday = dk === tKey;
              const isFused = !!state.fusedDays?.[dk];
              const dayNum = i + 1;
              return (
                <div key={i}
                  className="aspect-square rounded-md flex items-center justify-center"
                  style={{
                    background: isFused
                      ? `linear-gradient(155deg, #FFE9A0, ${GOLD_SOFT})`
                      : `linear-gradient(160deg, rgba(255,255,255,0.02), ${BG})`,
                    border: `1px solid ${isToday ? '#FFE9A0' : `${GOLD}33`}`,
                    boxShadow: isFused
                      ? `0 0 6px ${GOLD}77, inset 0 0 4px rgba(255,255,255,0.2)`
                      : isToday ? `0 0 8px ${GOLD}AA` : 'none',
                    color: isFused ? '#050308' : '#FFE9A0',
                    fontSize: 10,
                    fontFamily: 'Outfit, sans-serif',
                  }}
                  data-testid={`fuel-fuse-day-${dayNum}`}>
                  {dayNum}
                </div>
              );
            })}
          </div>

          {/* Fuse today CTA */}
          <div className="mt-4">
            {fusedToday ? (
              <div className="w-full py-2.5 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.24em] text-center"
                style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}`, color: '#FFE9A0' }}
                data-testid="fuel-fuse-already">
                ✓ Today Sealed · {state.fusedDays[tKey].percent}%
              </div>
            ) : (
              <button type="button" onClick={fuseToday}
                disabled={!canFuse}
                className="w-full py-2.5 rounded-full font-['Outfit'] uppercase text-[10px] tracking-[0.24em] disabled:opacity-40 flex items-center justify-center gap-1.5"
                style={{
                  background: canFuse ? `linear-gradient(135deg, #FFE9A0, ${GOLD_SOFT})` : 'transparent',
                  border: canFuse ? `1px solid #FFE9A0` : `1px solid ${GOLD}55`,
                  color: canFuse ? '#050308' : `${GOLD}AA`,
                  boxShadow: canFuse ? `0 0 14px ${GOLD}AA` : 'none',
                }}
                data-testid="fuel-fuse-today-btn">
                <Flame size={12}/>
                {canFuse
                  ? (isLate ? 'Fuse Tonight' : 'Fuse Today')
                  : `Need ${80 - pct}% more to fuse`}
              </button>
            )}
            {isLate && canFuse && !fusedToday && (
              <p className="mt-1.5 text-center font-['Cormorant_Garamond'] italic text-[11px]"
                style={{ color: `${GOLD}AA` }}>
                It&apos;s past 8 PM · seal your day before sleep.
              </p>
            )}
          </div>

          {complete && (
            <div className="mt-3 rounded-lg py-2 text-center"
              style={{ border: `1px solid #FFE9A0`, background: `${GOLD}18`, boxShadow: `0 0 14px ${GOLD}66` }}
              data-testid="fuel-fuse-complete">
              <p className="font-['Cormorant_Garamond'] text-base" style={{ color: '#FFE9A0' }}>
                Cycle Complete ✦
              </p>
              <p className="font-['Cormorant_Garamond'] italic text-xs" style={{ color: `${GOLD}CC` }}>
                {total} sealed days. Reset to run the next.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};


export const TheEnergy = ({ onClose }) => {
  const [loading, setLoading] = useState(true);
  const [today, setToday]     = useState(null);
  const [screen, setScreen]   = useState('menu'); // 'menu' | 'setup' | 'picker'
  const [subTab, setSubTab]   = useState('quota'); // 'quota' | 'fuse'

  // Back walks: sub-screen → menu → close.
  useHistoryClose(true, useCallback(() => {
    if (screen !== 'menu') {
      setScreen('menu');
      try { window.history.pushState({ __modal: Date.now() }, ''); } catch (_) {}
    } else {
      onClose();
    }
  }, [screen, onClose]));

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/energy/today`);
      if (!data.has_profile) {
        setToday(null);
        setScreen('setup');
      } else {
        setToday(data);
        setScreen((s) => (s === 'setup' ? 'menu' : s));
      }
    } catch (e) {
      setScreen('setup');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const handleSaved = async () => { await refresh(); setScreen('menu'); };

  const handleLog = async (entry) => {
    await axios.post(`${BACKEND_URL}/api/energy/log`, entry);
    await refresh();
    setScreen('menu');
  };

  const handleDelete = async (id) => {
    await axios.delete(`${BACKEND_URL}/api/energy/log/${id}`);
    await refresh();
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col"
      style={{ background: BG }}
      data-testid="energy-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 flex-shrink-0 border-b"
        style={{ borderColor: `${GOLD}22` }}>
        <BackPill onClick={onClose} accent="#5BE08A" testId="energy-close-btn"/>
        <p className="font-['Cinzel'] tracking-[0.22em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: '0 0 10px rgba(197,160,89,0.35)' }}>
          FUEL
        </p>
        <span className="w-14"/>
      </div>

      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <p className="font-['Outfit'] uppercase tracking-[0.35em]"
            style={{ color: GOLD, fontSize: 10 }}>Consulting the Ledger…</p>
        </div>
      ) : screen === 'setup' ? (
        <Setup initial={today?.quota} onSaved={handleSaved}/>
      ) : screen === 'menu' && today ? (
        <MasterMenu
          today={today}
          tab={subTab}
          setTab={setSubTab}
          dashboardNode={
            <Dashboard
              today={today}
              onReset={() => setScreen('setup')}
              onOpenPicker={() => setScreen('picker')}
              onDelete={handleDelete}
            />
          }
          fuseNode={<FuelFuse today={today} onBack={() => setSubTab('quota')}/>}
        />
      ) : screen === 'picker' && today ? (
        <FoodPicker
          gender={today.quota.gender}
          onBack={() => setScreen('menu')}
          onLog={handleLog}
        />
      ) : null}
    </div>
  );
};

/* Tile icon — bolt / lightning inside a crest */
export const EnergyTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(93,212,255,0.6))' }}>
    <defs>
      <linearGradient id="engIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#EAF7FF"/>
        <stop offset="0.5" stopColor="#5DD4FF"/>
        <stop offset="1" stopColor="#1B7FB5"/>
      </linearGradient>
    </defs>
    <path
      d="M13.5 2 L5 13 H11 L9.5 22 L19 10 H13 L14.6 2 Z"
      fill="url(#engIcon)" stroke="#0a0a0a" strokeWidth="0.6" strokeLinejoin="round"/>
  </svg>
);

export default TheEnergy;
